import { useState, useEffect } from "react";
import { Card, Table, Row, Col, Typography, List, Progress, Button } from "antd";
import { Wallet, ExternalLink } from "lucide-react";
import dayjs from "dayjs";
import { useNavigate } from "react-router";

import useAnalytics from "../hooks/useAnalytics";
import PageDocumentation from "../components/PageDocumentation";
import PeriodSelector from "../components/analytics/PeriodSelector";
import StatCard from "../components/analytics/StatCard";
import ExpensesPieChart from "../components/analytics/ExpensesPieChart";
import IncomeExpensesChart from "../components/analytics/IncomeExpensesChart";
import DebtModal from "../components/transfers/DebtModal";

const { Title } = Typography;

const CATEGORY_COLORS = {
  RENT: "#1890ff",
  SALARY: "#52c41a",
  PROFIT_WITHDRAWAL: "#faad14",
  WATER: "#13c2c2",
  ELECTRICITY: "#722ed1",
  GAS: "#eb2f96",
  EXPENSES: "#ff4d4f",
  OTHER: "#fa8c16",
};

function ExpensesAnalytics() {
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(30, "day"),
    dayjs(),
  ]);
  const [debtModal, setDebtModal] = useState({
    open: false,
    userId: null,
    type: null,
    extraInfo: null,
    transferId: null
  });

  const {
    expensesData,
    timelineData,
    allDebtsData,
    loading,
    getExpensesByCategory,
    getIncomeExpensesTimeline,
    getAllDebts,
  } = useAnalytics();

  useEffect(() => {
    if (dateRange[0] && dateRange[1]) {
      const startDate = dateRange[0].format("YYYY-MM-DD");
      const endDate = dateRange[1].format("YYYY-MM-DD");
      getExpensesByCategory(startDate, endDate);
      getIncomeExpensesTimeline(startDate, endDate);
      getAllDebts();
    }
  }, [dateRange]);

  const handlePeriodChange = (dates) => {
    if (dates && dates[0] && dates[1]) {
      setDateRange(dates);
    }
  };

  const totals = timelineData?.totals || {
    income: 0,
    expenses: 0,
    profit: 0,
  };

  const categories = expensesData?.categories || [];

  // Calcular total de deudas pendientes
  const totalDeudas = (allDebtsData || []).reduce(
    (sum, debt) => sum + (debt.pendingAmount ?? debt.totalAmount ?? 0),
    0
  );

  // Mapeo de tipos de deuda a nombres en español
  const tiposDeuda = {
    USER_SELLER: "Vendedor",
    USER_GUIDE: "Guía",
    USER_COORDINATOR: "Coordinador",
    USER_TRANSPORTER: "Transportista",
    COMPANY: "Compañía",
  };

  const handleRendirDinero = (record) => {
    if (record.transferType === "DEBT") {
      setDebtModal({
        open: true,
        userId: record.id,
        type: record.type,
        extraInfo: record.extraInfo,
        transferId: null
      });
    } else {
      setDebtModal({
        open: true,
        userId: null,
        type: null,
        extraInfo: null,
        transferId: record.id
      });
    }
  };

  // Columnas para tabla de deudas pendientes
  const columnasDeudas = [
    {
      title: "Tipo",
      dataIndex: "type",
      key: "type",
      width: 120,
      render: (value) => tiposDeuda[value] || value,
      filters: Object.entries(tiposDeuda).map(([value, text]) => ({ text, value })),
      onFilter: (value, record) => record.type === value,
    },
    {
      title: "Nombre",
      dataIndex: "title",
      key: "title",
      width: 180,
      ellipsis: true,
    },
    {
      title: "Fecha",
      dataIndex: "extraInfo",
      key: "extraInfo",
      width: 100,
      render: (value) => value ? dayjs(value).format("DD/MM/YYYY") : "-",
      sorter: (a, b) => dayjs(a.extraInfo).unix() - dayjs(b.extraInfo).unix(),
    },
    {
      title: "Cobrado",
      dataIndex: "incomeAmount",
      key: "incomeAmount",
      width: 100,
      align: "right",
      render: (value) => (
        <span className="text-green-600">${value?.toLocaleString() || 0}</span>
      ),
    },
    {
      title: "Comisión",
      dataIndex: "commissionAmount",
      key: "commissionAmount",
      width: 100,
      align: "right",
      render: (value) => (
        <span className="text-blue-600">${value?.toLocaleString() || 0}</span>
      ),
    },
    {
      title: "Pendiente",
      dataIndex: "totalAmount",
      key: "totalAmount",
      width: 120,
      align: "right",
      sorter: (a, b) => (a.pendingAmount ?? a.totalAmount ?? 0) - (b.pendingAmount ?? b.totalAmount ?? 0),
      render: (value, record) => {
        const monto = record.pendingAmount ?? value;
        return (
          <span className={monto > 0 ? "text-orange-500 font-bold" : "text-green-600"}>
            ${monto?.toLocaleString() || 0}
          </span>
        );
      },
    },
    {
      title: "Acción",
      key: "action",
      width: 120,
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          onClick={() => handleRendirDinero(record)}
        >
          Rendir dinero
        </Button>
      ),
    },
  ];

  const isLoading = loading.expenses || loading.timeline;

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Wallet size={24} className="text-blue-600" />
          <Title level={3} className="!mb-0">
            Gastos e Ingresos
          </Title>
        </div>
        <PageDocumentation docKey="expenses-analytics" />
      </div>

      <PeriodSelector
        value={dateRange}
        onChange={handlePeriodChange}
        loading={isLoading}
      />

      {/* Stats Cards */}
      <Row gutter={[16, 16]} className="mb-4">
        <Col xs={24} sm={6}>
          <StatCard
            title="Ingresos Totales"
            value={totals.income}
            type="income"
            loading={loading.timeline}
            tooltip="Suma de todos los pagos recibidos (señas de pasajeros, cobros de excursiones, etc.) en el período seleccionado"
          />
        </Col>
        <Col xs={24} sm={6}>
          <StatCard
            title="Gastos Totales"
            value={totals.expenses}
            type="expenses"
            loading={loading.timeline}
            tooltip="Suma de todos los gastos registrados (alquiler, sueldos, retiros, servicios, gastos operativos, etc.) en el período seleccionado"
          />
        </Col>
        <Col xs={24} sm={6}>
          <StatCard
            title="Balance"
            value={totals.profit}
            type={totals.profit >= 0 ? "profit" : "expenses"}
            loading={loading.timeline}
            tooltip="Diferencia entre Ingresos Totales y Gastos Totales (Ingresos - Gastos)"
          />
        </Col>
        <Col xs={24} sm={6}>
          <StatCard
            title="Deudas Pendientes"
            value={totalDeudas}
            type="warning"
            loading={loading.debts}
            tooltip="Suma de todas las deudas por cobrar: rendiciones pendientes de vendedores, guías, coordinadores y transportistas, más deudas de compañías"
          />
        </Col>
      </Row>

      {/* Charts Row */}
      <Row gutter={[16, 16]} className="mb-4">
        <Col xs={24} lg={12}>
          <ExpensesPieChart
            data={categories}
            loading={loading.expenses}
          />
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Desglose por Categoria" loading={loading.expenses}>
            <List
              dataSource={categories}
              renderItem={(item) => (
                <List.Item>
                  <div className="w-full">
                    <div className="flex justify-between mb-1">
                      <span className="font-medium">{item.label}</span>
                      <span className="font-bold">
                        ${item.amount.toLocaleString()} ({item.percentage}%)
                      </span>
                    </div>
                    <Progress
                      percent={item.percentage}
                      showInfo={false}
                      strokeColor={CATEGORY_COLORS[item.type] || "#999"}
                      size="small"
                    />
                  </div>
                </List.Item>
              )}
              locale={{ emptyText: "No hay gastos en este periodo" }}
            />
          </Card>
        </Col>
      </Row>

      {/* Timeline Chart */}
      <Row gutter={[16, 16]} className="mb-4">
        <Col xs={24}>
          <IncomeExpensesChart
            data={timelineData?.by_date || []}
            loading={loading.timeline}
          />
        </Col>
      </Row>

      {/* Timeline Table */}
      <Card title="Detalle por Dia">
        <Table
          columns={[
            {
              title: "Fecha",
              dataIndex: "date",
              key: "date",
              width: 100,
              fixed: "left",
              render: (value) => dayjs(value).format("DD/MM/YYYY"),
            },
            {
              title: "Ingresos",
              dataIndex: "income",
              key: "income",
              width: 100,
              align: "right",
              render: (value) => (
                <span className="text-green-600">${value.toLocaleString()}</span>
              ),
            },
            {
              title: "Gastos",
              dataIndex: "expenses",
              key: "expenses",
              width: 100,
              align: "right",
              render: (value) => (
                <span className="text-red-500">${value.toLocaleString()}</span>
              ),
            },
            {
              title: "Balance",
              dataIndex: "profit",
              key: "profit",
              width: 100,
              align: "right",
              render: (value) => (
                <span className={value >= 0 ? "text-blue-600 font-bold" : "text-red-600 font-bold"}>
                  ${value.toLocaleString()}
                </span>
              ),
            },
            // Columnas dinamicas por caja
            ...(timelineData?.boxes || []).map((box) => ({
              title: box.name,
              dataIndex: `box_${box.id}`,
              key: `box_${box.id}`,
              width: 110,
              align: "right",
              render: (value) => (
                <span className={value >= 0 ? "text-green-600" : "text-red-500"}>
                  ${(value || 0).toLocaleString()}
                </span>
              ),
            })),
            {
              title: "",
              key: "actions",
              width: 120,
              align: "center",
              fixed: "right",
              render: (_, record) => {
                const d = dayjs(record.date);
                return (
                  <Button
                    type="link"
                    size="small"
                    icon={<ExternalLink size={14} />}
                    onClick={() => navigate(`/caja?year=${d.year()}&month=${d.month() + 1}&day=${d.date()}`)}
                  >
                    Ver detalle
                  </Button>
                );
              },
            },
          ]}
          dataSource={timelineData?.by_date || []}
          rowKey="date"
          loading={loading.timeline}
          pagination={{ pageSize: 10 }}
          size="small"
          scroll={{ x: 600 + (timelineData?.boxes?.length || 0) * 110 }}
          summary={() =>
            timelineData?.by_date?.length > 0 ? (
              <Table.Summary fixed>
                <Table.Summary.Row className="bg-gray-100 font-bold">
                  <Table.Summary.Cell index={0}>TOTAL</Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="right">
                    <span className="text-green-600">
                      ${totals.income.toLocaleString()}
                    </span>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} align="right">
                    <span className="text-red-500">
                      ${totals.expenses.toLocaleString()}
                    </span>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3} align="right">
                    <span className={totals.profit >= 0 ? "text-blue-600" : "text-red-600"}>
                      ${totals.profit.toLocaleString()}
                    </span>
                  </Table.Summary.Cell>
                  {(timelineData?.boxes || []).map((box, idx) => (
                    <Table.Summary.Cell key={box.id} index={4 + idx} align="right">
                      <span className={(totals[`box_${box.id}`] || 0) >= 0 ? "text-green-600" : "text-red-500"}>
                        ${(totals[`box_${box.id}`] || 0).toLocaleString()}
                      </span>
                    </Table.Summary.Cell>
                  ))}
                  <Table.Summary.Cell index={4 + (timelineData?.boxes?.length || 0)} />
                </Table.Summary.Row>
              </Table.Summary>
            ) : null
          }
        />
      </Card>

      {/* Tabla de Deudas Pendientes */}
      <Card title="Deudas Pendientes" className="mt-4">
        <Table
          columns={columnasDeudas}
          dataSource={allDebtsData || []}
          rowKey={(record) => `${record.id}-${record.type}-${record.extraInfo}`}
          loading={loading.debts}
          pagination={{ pageSize: 15 }}
          size="small"
          scroll={{ x: 800 }}
          onRow={(record) => ({
            onClick: (e) => {
              if (e.target.closest('.ant-btn, .ant-select, a')) return;
              handleRendirDinero(record);
            },
            style: { cursor: 'pointer' }
          })}
          summary={() =>
            allDebtsData?.length > 0 ? (
              <Table.Summary fixed>
                <Table.Summary.Row className="bg-gray-100 font-bold">
                  <Table.Summary.Cell index={0}>TOTAL</Table.Summary.Cell>
                  <Table.Summary.Cell index={1}>
                    {allDebtsData?.length} registros
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} />
                  <Table.Summary.Cell index={3} />
                  <Table.Summary.Cell index={4} />
                  <Table.Summary.Cell index={5} align="right">
                    <span className="text-orange-500">
                      ${totalDeudas?.toLocaleString() || 0}
                    </span>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={6} />
                </Table.Summary.Row>
              </Table.Summary>
            ) : null
          }
        />
      </Card>

      <DebtModal
        open={debtModal.open}
        onClose={() => setDebtModal({ open: false, userId: null, type: null, extraInfo: null, transferId: null })}
        userId={debtModal.userId}
        type={debtModal.type}
        extraInfo={debtModal.extraInfo}
        transferId={debtModal.transferId}
        onSuccess={() => {
          setDebtModal({ open: false, userId: null, type: null, extraInfo: null, transferId: null });
          getAllDebts();
        }}
      />
    </div>
  );
}

export default ExpensesAnalytics;
